import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Snackbar,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import {
  Add,
  Search,
  Visibility,
  AttachMoney,
  ArrowBack,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import PayoutDialog from '../components/PayoutDialog';
import PayoutCalculator from '../components/PayoutCalculator';

const VendorPayouts = () => {
  const navigate = useNavigate();
  const [payouts, setPayouts] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [vendorFilter, setVendorFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Dialogs
  const [calculatorOpen, setCalculatorOpen] = useState(false);
  const [payoutDialogOpen, setPayoutDialogOpen] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [payoutData, setPayoutData] = useState(null);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchPayouts();
    fetchVendors();
  }, [page, rowsPerPage, vendorFilter, statusFilter]);

  const fetchPayouts = async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        per_page: rowsPerPage,
      };
      if (vendorFilter) params.vendor_id = vendorFilter;
      if (statusFilter) params.status = statusFilter;

      const response = await api.get('/api/consignments/payouts', { params });
      setPayouts(response.data.payouts || []);
      setTotalItems(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching payouts:', error);
      showSnackbar('Error loading payouts', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchVendors = async () => {
    try {
      const response = await api.get('/api/admin/clients');
      setVendors(response.data.clients || []);
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  };

  const handleCalculatePayout = (vendor) => {
    setSelectedVendor(vendor);
    setCalculatorOpen(true);
  };

  const handleProcessPayout = (data) => {
    setPayoutData(data);
    setCalculatorOpen(false);
    setPayoutDialogOpen(true);
  };

  const handlePayoutSuccess = () => {
    setPayoutDialogOpen(false);
    setPayoutData(null);
    setSelectedVendor(null);
    fetchPayouts();
    showSnackbar('Payout processed successfully', 'success');
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const filteredPayouts = payouts.filter((payout) => {
    const matchesSearch =
      !searchQuery ||
      payout.vendor?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      payout.reference_number?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button startIcon={<ArrowBack />} onClick={() => navigate('/consignments')}>
            Back to Consignments
          </Button>
          <Typography variant="h4">Vendor Payouts</Typography>
        </Box>
        <Button
          variant="contained"
          onClick={() => setCalculatorOpen(true)}
          startIcon={<AttachMoney />}
        >
          Calculate New Payout
        </Button>
      </Box>

      {/* Filters */}
      <Card sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              placeholder="Search payouts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth>
              <InputLabel>Vendor</InputLabel>
              <Select
                value={vendorFilter}
                onChange={(e) => setVendorFilter(e.target.value)}
                label="Vendor"
              >
                <MenuItem value="">All Vendors</MenuItem>
                {vendors.map((vendor) => (
                  <MenuItem key={vendor.id} value={vendor.id}>
                    {vendor.user?.first_name} {vendor.user?.last_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="paid">Paid</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Card>

      {/* Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Vendor</TableCell>
                <TableCell>Payout Date</TableCell>
                <TableCell>Items Count</TableCell>
                <TableCell>Total Amount</TableCell>
                <TableCell>Payment Method</TableCell>
                <TableCell>Reference</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : filteredPayouts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No payouts found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredPayouts.map((payout) => (
                  <TableRow key={payout.id} hover>
                    <TableCell>{payout.vendor?.name || 'Unknown Vendor'}</TableCell>
                    <TableCell>
                      {payout.payout_date
                        ? new Date(payout.payout_date).toLocaleDateString()
                        : '-'}
                    </TableCell>
                    <TableCell>{payout.items_count}</TableCell>
                    <TableCell>
                      <Typography variant="body1" fontWeight="bold" color="success.main">
                        ${parseFloat(payout.total_amount || 0).toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={payout.payment_method} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>{payout.reference_number || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={payout.status}
                        size="small"
                        color={
                          payout.status === 'paid'
                            ? 'success'
                            : payout.status === 'approved'
                            ? 'info'
                            : payout.status === 'cancelled'
                            ? 'error'
                            : 'default'
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => {
                            /* Navigate to payout detail */
                          }}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={totalItems}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Card>

      {/* Payout Calculator Dialog */}
      {calculatorOpen && (
        <PayoutCalculator
          open={calculatorOpen}
          vendor={selectedVendor}
          onClose={() => {
            setCalculatorOpen(false);
            setSelectedVendor(null);
          }}
          onProceed={handleProcessPayout}
        />
      )}

      {/* Payout Dialog */}
      {payoutDialogOpen && payoutData && (
        <PayoutDialog
          open={payoutDialogOpen}
          payoutData={payoutData}
          onClose={() => setPayoutDialogOpen(false)}
          onSuccess={handlePayoutSuccess}
        />
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default VendorPayouts;
